import { AppError } from '../../../api/errors.js';
import type {
  DatasetCreateDataType,
  MeetingEntryFieldKey,
} from '../../../constants/index.js';
import {
  DATASET_CREATE_DATA_TYPES,
  MEETING_ENTRY_FIELDS,
} from '../../../constants/index.js';
import type { DataEntryFileType } from '../../../services/dataHubService.interface.js';
import type { DataHubService } from '../../../services/dataHubService.js';
import type { UsageContextOverrides } from '../../../services/usageContext.interface.js';
import {
  USAGE_EXTRA_KEYS,
  withCorrelationId,
} from '../../../services/usageContext.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import type { IScenario } from '../../../telemetry/scenarios.interface.js';
import { runConcurrent, withRetry } from '../../../utils/concurrency.js';

import type {
  EntryMetadata,
  ParsedFolderEntry,
} from './UploadFolderDialog.interface.js';

// ── Types ──────────────────────────────────────────────────────────────────

export interface DatasetUploadProgress {
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  currentEntry: string;
  phase: 'creating-entries' | 'uploading' | 'done';
}

export interface DatasetUploadResult {
  success: boolean;
  errors: string[];
  uploadedFileCount: number;
  createdEntryCount: number;
}

// ── Constants ──────────────────────────────────────────────────────────────

export const CONCURRENT_UPLOADS = 5;
export const MAX_RETRIES = 3;

export const METADATA_FILE = 'metadata.json';
export const META_JSON_SUFFIX = '.meta.json';

const MIN_SUBFOLDER_DEPTH = 3;
const HTTP_STATUS_NOT_FOUND = 404;

const VALID_FILE_TYPES = new Set<string>([
  'generic',
  'videoStream',
  'audioStream',
  'transcript',
]);

const API_DATA_TYPE_TO_LABEL: Partial<Record<string, DatasetCreateDataType>> = {
  generic: 'Generic',
  meetingData: 'Meeting Data',
  chatData: 'Chat Data',
  fileData: 'File Data',
  annotationData: 'Annotation Data',
};

// ── Folder parsing ─────────────────────────────────────────────────────

export function parseFolderFiles(files: FileList): ParsedFolderEntry[] {
  const entryMap = new Map<string, File[]>();
  const fileArray = Array.from(files);

  for (const file of fileArray) {
    const relativePath = file.webkitRelativePath;
    if (!relativePath) continue;

    const parts = relativePath.split('/');
    // parts[0] = selected folder, parts[1] = level-1 subfolder (= entry name)
    // Only files inside level-1 subfolders (depth >= 3) become entry files.
    // Root-level files (depth 2) like metadata.json are skipped.
    if (parts.length < MIN_SUBFOLDER_DEPTH) continue;

    const entryName = parts[1]!;
    const existing = entryMap.get(entryName) ?? [];
    existing.push(file);
    entryMap.set(entryName, existing);
  }

  // Fallback: flat folder with no subfolders — treat all files as one entry
  if (entryMap.size === 0 && fileArray.length > 0) {
    const firstFile = fileArray[0]!;
    const rootName = firstFile.webkitRelativePath.split('/')[0] ?? 'upload';
    return [{ name: rootName, files: fileArray }];
  }

  const result = Array.from(entryMap.entries()).map(([name, entryFiles]) => ({
    name,
    files: entryFiles,
  }));

  return result;
}

// ── Metadata extraction ─────────────────────────────────────────────────

function isMetadataFile(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return lower === METADATA_FILE || lower.endsWith(META_JSON_SUFFIX);
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsText(file);
  });
}

async function readFileAsJson(file: File): Promise<unknown> {
  const text = await readFileAsText(file);
  return JSON.parse(text) as unknown;
}

function parseEntryMetadataJson(raw: unknown): EntryMetadata {
  if (typeof raw !== 'object' || raw === null) return {};

  const obj = raw as Record<string, unknown>;
  const result: EntryMetadata = {};

  if (typeof obj.name === 'string' && obj.name.length > 0) {
    result.name = obj.name;
  }
  if (typeof obj.description === 'string') {
    result.description = obj.description;
  }
  if (typeof obj.properties === 'object' && obj.properties !== null) {
    result.properties = obj.properties as Record<string, unknown>;
  }
  if (typeof obj.dataType === 'string') {
    const mapped = API_DATA_TYPE_TO_LABEL[obj.dataType];
    if (mapped && DATASET_CREATE_DATA_TYPES.includes(mapped)) {
      result.dataType = mapped;
    }
  }

  // Meeting entries carry their scalar fields (title, language, date, organizer
  // AAD id) at the top level of metadata.json, alongside the free-form
  // `properties` object. Extract the recognised, non-empty string fields so the
  // bulk-upload create request populates them the same way the single-entry
  // Add Data Entry dialog does.
  const meetingFields: Partial<Record<MeetingEntryFieldKey, string>> = {};
  for (const { key } of MEETING_ENTRY_FIELDS) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim() !== '') {
      meetingFields[key] = value.trim();
    }
  }
  if (Object.keys(meetingFields).length > 0) {
    result.meetingFields = meetingFields;
  }

  return result;
}

function parseSidecarFileType(raw: unknown): DataEntryFileType {
  if (typeof raw !== 'object' || raw === null) return 'generic';
  const obj = raw as Record<string, unknown>;
  const ft = obj.fileType;
  if (typeof ft === 'string' && VALID_FILE_TYPES.has(ft)) {
    return ft as DataEntryFileType;
  }
  return 'generic';
}

export async function extractMetadata(
  entries: ParsedFolderEntry[],
): Promise<ParsedFolderEntry[]> {
  const results: ParsedFolderEntry[] = [];

  for (const entry of entries) {
    const metadataFile = entry.files.find(
      (f) => f.name.toLowerCase() === METADATA_FILE,
    );
    const sidecarFiles = entry.files.filter(
      (f) =>
        f.name.toLowerCase().endsWith(META_JSON_SUFFIX) &&
        f.name.toLowerCase() !== METADATA_FILE,
    );
    const dataFiles = entry.files.filter((f) => !isMetadataFile(f.name));

    let metadata: EntryMetadata | undefined;
    if (metadataFile) {
      try {
        const raw = await readFileAsJson(metadataFile);
        metadata = parseEntryMetadataJson(raw);
      } catch {
        // malformed metadata.json — ignore, use defaults
      }
    }

    let fileTypes: Map<string, DataEntryFileType> | undefined;
    if (sidecarFiles.length > 0) {
      fileTypes = new Map();
      for (const sidecar of sidecarFiles) {
        const targetName = sidecar.name.slice(
          0,
          sidecar.name.length - META_JSON_SUFFIX.length,
        );
        const matchedFile = dataFiles.find((f) => {
          const dotIdx = f.name.lastIndexOf('.');
          const base = dotIdx > 0 ? f.name.slice(0, dotIdx) : f.name;
          return base === targetName;
        });
        if (matchedFile) {
          try {
            const raw = await readFileAsJson(sidecar);
            fileTypes.set(matchedFile.name, parseSidecarFileType(raw));
          } catch {
            // malformed sidecar — default to generic
          }
        }
      }
      if (fileTypes.size === 0) fileTypes = undefined;
    }

    results.push({
      name: metadata?.name ?? entry.name,
      files: dataFiles,
      metadata,
      fileTypes,
    });
  }

  return results;
}

// ── Per-entry batch upload ─────────────────────────────────────────────────
// 1. Batch POST  → get all upload URLs for the entry in one call
// 2. Concurrent  → upload blobs in parallel (separate ADLS URLs, no conflict)
// 3. Batch PUT   → complete all files atomically (single version update)

export async function uploadEntryFiles(
  service: DataHubService,
  dataSetId: string,
  dataId: string,
  files: File[],
  fileTypeMap: Map<string, DataEntryFileType> | undefined,
  onFileComplete: () => void,
  onFileFailed: (fileName: string, error: unknown) => void,
  signal?: AbortSignal,
  subTypeMap?: Map<string, string>,
  scenario?: IScenario,
  entryNumber?: number,
): Promise<void> {
  const fileNames = files.map((f) => f.name);
  const entryData = {
    ...(entryNumber === undefined ? {} : { entry_number: entryNumber }),
    requested_files: fileNames.length,
  };
  scenario?.mark('upload_urls_requested', entryData);
  const urlResult = await withRetry(
    () => service.batchGenerateFileUploadUrls(dataSetId, dataId, fileNames),
    MAX_RETRIES,
  );
  scenario?.mark('upload_urls_generated', {
    ...entryData,
    generated_urls: urlResult.succeededItems.length,
    failed_urls: urlResult.failedItems.length,
  });

  if (signal?.aborted) return;

  // Surface every URL-generation failure, even when other files in the same
  // entry succeeded. Previously these were only reported when *all* files
  // failed, so partial failures were silently dropped from the results.
  const failedNames = new Set<string>();
  for (const failed of urlResult.failedItems) {
    const name = failed.fileName ?? 'unknown';
    failedNames.add(name);
    onFileFailed(
      name,
      new Error(failed.errorMessage ?? 'Failed to generate upload URL'),
    );
  }

  if (urlResult.succeededItems.length === 0) return;

  // The batch response carries no per-file identity ({ fileId, uploadUrl }), so
  // upload URLs are correlated to files positionally, in request order. That is
  // only sound when the succeeded URLs line up 1:1 with the files that did not
  // fail. Guard the count: on any mismatch we cannot know which URL belongs to
  // which file, so we fail the affected files rather than risk uploading one
  // file's bytes to another file's blob.
  const pendingFiles = files.filter((f) => !failedNames.has(f.name));
  if (urlResult.succeededItems.length !== pendingFiles.length) {
    for (const file of pendingFiles) {
      onFileFailed(
        file.name,
        new Error(
          `Could not correlate upload URLs to files: batch returned ${urlResult.succeededItems.length} URLs for ${pendingFiles.length} files.`,
        ),
      );
    }
    return;
  }

  const uploadPairs = pendingFiles.map((file, index) => ({
    file,
    upload: urlResult.succeededItems[index]!,
  }));

  const uploadResults = await runConcurrent(
    uploadPairs,
    CONCURRENT_UPLOADS,
    async ({ file, upload }) => {
      if (signal?.aborted) return null;

      try {
        await withRetry(
          () => service.uploadFileToBlob(upload.uploadUrl, file),
          MAX_RETRIES,
        );
        onFileComplete();
        return {
          id: upload.fileId,
          fileName: file.name,
          fileType:
            fileTypeMap?.get(file.name) ?? ('generic' as DataEntryFileType),
          subType: subTypeMap?.get(file.name),
          fileSizeInBytes: file.size,
        };
      } catch (err) {
        onFileFailed(file.name, err);
        return null;
      }
    },
  );

  if (signal?.aborted) return;

  const completedFiles = uploadResults.filter(
    (r): r is NonNullable<typeof r> => r !== null,
  );
  scenario?.mark('blob_uploads_finished', {
    ...(entryNumber === undefined ? {} : { entry_number: entryNumber }),
    uploaded_files: completedFiles.length,
    failed_files: uploadPairs.length - completedFiles.length,
  });

  if (completedFiles.length > 0) {
    // Partial completion is intentional and matches the batch endpoint's
    // contract: files that failed to upload are simply omitted, and the single
    // version update commits exactly the files that were written to blob. The
    // retry wraps only the control-plane call so a transient failure does not
    // discard an already-uploaded batch.
    scenario?.mark('entry_commit_started', {
      ...(entryNumber === undefined ? {} : { entry_number: entryNumber }),
      file_count: completedFiles.length,
    });
    await withRetry(
      () => service.batchCompleteFileUploads(dataSetId, dataId, completedFiles),
      MAX_RETRIES,
    );
    scenario?.mark('entry_files_committed', {
      ...(entryNumber === undefined ? {} : { entry_number: entryNumber }),
      file_count: completedFiles.length,
    });
  }
}

// ── Main upload orchestrator ──────────────────────────────────────────────

export async function uploadDatasetFolder(
  service: DataHubService,
  dataSetId: string,
  defaultDataType: DatasetCreateDataType,
  folderEntries: ParsedFolderEntry[],
  onProgress: (progress: DatasetUploadProgress) => void,
  signal?: AbortSignal,
  scenario?: IScenario,
): Promise<DatasetUploadResult> {
  if (signal?.aborted) return abortedResult();

  const errors: string[] = [];
  let totalFiles = 0;
  let completedFiles = 0;
  let failedFiles = 0;

  const reportProgress = (
    currentEntry: string,
    phase: DatasetUploadProgress['phase'],
  ): void => {
    onProgress({
      totalFiles,
      completedFiles,
      failedFiles,
      currentEntry,
      phase,
    });
  };

  // ── Step 1: Batch create data entries ──────────────────────────────────
  reportProgress('', 'creating-entries');

  let batchResult;
  const entryRequests = folderEntries.map((e) => ({
    name: e.name,
    dataType: e.metadata?.dataType ?? defaultDataType,
    description: e.metadata?.description,
    properties: e.metadata?.properties,
    ...e.metadata?.meetingFields,
  }));

  // Tag the batch-create as one "Upload Data (folder)" TEDH usage event (D2:
  // one summary event per folder upload, carrying the planned counts). The
  // counts are known up-front from the parsed folder, before any file uploads.
  const plannedFileCount = folderEntries.reduce(
    (sum, e) => sum + e.files.length,
    0,
  );
  const uploadUsage: UsageContextOverrides = {
    extras: withCorrelationId(scenario?.queryId, {
      [USAGE_EXTRA_KEYS.FILE_COUNT]: String(plannedFileCount),
      [USAGE_EXTRA_KEYS.ENTRY_COUNT]: String(entryRequests.length),
    }),
  };

  scenario?.mark('entries_create_started', {
    entry_count: entryRequests.length,
    total_files: plannedFileCount,
  });
  try {
    batchResult = await service.batchAddDataEntries(
      dataSetId,
      entryRequests,
      uploadUsage,
    );
  } catch (err) {
    if (err instanceof AppError && err.statusCode === HTTP_STATUS_NOT_FOUND) {
      scenario?.mark('draft_create_started');
      await service.createDraftDataset(dataSetId, uploadUsage);
      scenario?.mark('draft_created');
      batchResult = await service.batchAddDataEntries(
        dataSetId,
        entryRequests,
        uploadUsage,
      );
    } else {
      throw err;
    }
  }

  if (signal?.aborted) return abortedResult();

  for (const f of batchResult.failed) {
    errors.push(
      `Entry "${f.dataId ?? 'unknown'}": ${f.errorMessage ?? 'creation failed'}`,
    );
  }

  const createdEntries = batchResult.succeeded;
  if (createdEntries.length === 0) {
    return {
      success: false,
      errors: errors.length > 0 ? errors : ['All entries failed to create.'],
      uploadedFileCount: 0,
      createdEntryCount: 0,
    };
  }

  const entryLookup = new Map<string, ParsedFolderEntry>();
  for (const parsed of folderEntries) {
    entryLookup.set(parsed.name, parsed);
  }

  totalFiles = createdEntries.reduce(
    (sum, e) => sum + (entryLookup.get(e.name ?? '')?.files.length ?? 0),
    0,
  );

  // Milestone: control-plane entry creation done; file uploads begin next.
  scenario?.mark('entries_created', {
    entry_count: createdEntries.length,
    total_files: totalFiles,
  });

  // ── Step 2: Upload files ──────────────────────────────────────────────
  // Entries run concurrently (different dataIds — no shared state).
  // Within each entry: batch POST for URLs → concurrent blob uploads →
  // single batch PUT to complete (atomic version update, no race).
  const indexedEntries = createdEntries.map((entry, index) => ({
    entry,
    entryNumber: index + 1,
  }));
  await runConcurrent(
    indexedEntries,
    CONCURRENT_UPLOADS,
    async ({ entry, entryNumber }) => {
      const parsed = entryLookup.get(entry.name ?? '');
      const files = parsed?.files ?? [];
      if (files.length === 0) return;
      if (signal?.aborted) return;

      const entryName = entry.name ?? '';
      reportProgress(entryName, 'uploading');

      try {
        await uploadEntryFiles(
          service,
          dataSetId,
          entry.dataId,
          files,
          parsed?.fileTypes,
          () => {
            completedFiles++;
            reportProgress(entryName, 'uploading');
          },
          (fileName, err) => {
            failedFiles++;
            errors.push(
              `"${entryName}/${fileName}": ${err instanceof Error ? err.message : 'Upload failed'} (after ${MAX_RETRIES} retries)`,
            );
            reportProgress(entryName, 'uploading');
          },
          signal,
          undefined,
          scenario,
          entryNumber,
        );
      } catch (err) {
        failedFiles += files.length;
        const classification = classifyScenarioError(err);
        scenario?.mark('entry_upload_failed', {
          entry_number: entryNumber,
          ...classification.data,
        });
        errors.push(
          `"${entryName}": ${err instanceof Error ? err.message : 'Upload failed'}`,
        );
      }
    },
  );

  // Milestone: all data-plane blob uploads settled (successes + failures).
  scenario?.mark('files_uploaded', {
    completed_files: completedFiles,
    failed_files: failedFiles,
  });

  onProgress({
    totalFiles,
    completedFiles,
    failedFiles,
    currentEntry: '',
    phase: 'done',
  });

  return {
    success: errors.length === 0,
    errors,
    uploadedFileCount: completedFiles,
    createdEntryCount: createdEntries.length,
  };
}

function abortedResult(): DatasetUploadResult {
  return {
    success: false,
    errors: ['Upload was cancelled.'],
    uploadedFileCount: 0,
    createdEntryCount: 0,
  };
}
